/**
 * client/src/store/useTimerStore.ts
 *
 * Modern, hubId-based Timer store.
 * - Uses hubId from window.__currentHubId
 * - Uses WebSocket (wss://host/?hubId=...) + BroadcastChannel for realtime
 * - Persists canonical state to Neon via POST /api/timer-state
 * - Clean, explicit functions for start/pause/reset/restart/create/delete
 */

import { create } from "zustand";
import { TimerStateData } from "@shared/schema";
import { formatTime } from "@/lib/formatTime";

/** Small helper so TS knows about the global the app sets. */
declare global {
  interface Window {
    __currentHubId?: string;
    timerGlobalChannel?: BroadcastChannel;
  }
}

type TimerId = string | number;

export interface TimerSegment {
  id?: string;
  name: string;
  durationSeconds: number;
  image?: string | null;
}

export interface TimerState {
  id: TimerId;
  duration: number;
  timeRemaining: number;
  isRunning: boolean;
  isPaused: boolean;
  status?: 'idle' | 'running' | 'paused' | 'stopped';
  lastUpdated?: number;
  startedAt?: number; // Timestamp when timer started running (for accurate countdown)
  endsAtMs?: number; // AUTHORITATIVE end timestamp - used for refresh recovery
  title?: string;
  hubId?: string;
  formattedTime?: string;
  image?: string; // Timer image URL
  type?: 'circular' | 'rectangular'; // Timer shape type
  segments?: TimerSegment[]; // Segments for rectangular timers
  sessionId?: string;
  completionKey?: string;
}

interface TimerStoreState {
  timers: Record<string | number, TimerState>;
  initWebSocket: () => void;
  initBroadcast: () => void;
  createTimer: (timerData: Partial<TimerState> & { id: TimerId; duration: number; title?: string }) => Promise<void>;
  startTimer: (id: TimerId) => Promise<void>;
  pauseTimer: (id: TimerId) => Promise<void>;
  resetTimer: (id: TimerId) => Promise<void>;
  restartTimer: (id: TimerId) => Promise<void>;
  updateTimer: (id: TimerId, updates: Partial<TimerState>) => Promise<void>;
  updateTimerState: (id: TimerId, updates: Partial<TimerState>) => void;
  getTimer: (id: TimerId) => TimerState | undefined;
  clearTimer: (id: TimerId) => void;
  deleteTimer: (id: TimerId) => Promise<void>;
  broadcastTimerUpdate: (timerId: TimerId, timerData?: Partial<TimerState>) => void;
  startLocalCountdown: (id: TimerId, startedAtOverride?: number) => void;
  hydrateRunningTimer: (id: TimerId, endsAtMs: number) => void;
  isTicking: (id: TimerId) => boolean;
  seedFromSession: (session: {
    timerId: string;
    sessionId?: string;
    image?: string;
    totalDuration?: number;
    durationSeconds?: number;
    title?: string;
    startedAt?: string;
    status: string;
    timeRemaining?: number;
    endsAtMs?: number; // AUTHORITATIVE end timestamp for running timers
  }) => void;
}

const emittedCompletionOccurrences = new Set<string>();

function getCompletionOccurrenceKey(
  id: TimerId,
  timer?: Partial<TimerState>,
  endsAtMs?: number,
): string {
  if (timer?.completionKey) return timer.completionKey;
  if (timer?.sessionId) return `session:${timer.sessionId}`;
  if (endsAtMs !== undefined && Number.isFinite(endsAtMs)) return `ends:${endsAtMs}`;
  if (timer?.endsAtMs !== undefined && Number.isFinite(timer.endsAtMs)) {
    return `ends:${timer.endsAtMs}`;
  }
  if (timer?.startedAt !== undefined) return `started:${String(timer.startedAt)}`;
  if (timer?.lastUpdated !== undefined) return `updated:${timer.lastUpdated}`;
  return `unknown:${Date.now()}`;
}

function dispatchTimerCompleted(
  id: TimerId,
  timer?: Partial<TimerState>,
  endsAtMs?: number,
): void {
  const completionKey = getCompletionOccurrenceKey(id, timer, endsAtMs);
  const eventKey = `${String(id)}:${completionKey}`;
  if (emittedCompletionOccurrences.has(eventKey)) return;
  emittedCompletionOccurrences.add(eventKey);

  window.dispatchEvent(new CustomEvent('timer-completed', {
    detail: {
      timerId: id,
      completionKey,
      sessionId: timer?.sessionId,
    },
  }));
}

const PING_INTERVAL_MS = 30000;
const RECONNECT_BASE_MS = 1000; // exponential backoff base

// Broadcast channel names
const LOCAL_CHANNEL_NAME = "timer-updates";
const GLOBAL_CHANNEL_NAME = "timer-global-updates";

const localChannel = typeof window !== "undefined" ? new BroadcastChannel(LOCAL_CHANNEL_NAME) : (null as any);
const globalChannel = typeof window !== "undefined" ? new BroadcastChannel(GLOBAL_CHANNEL_NAME) : (null as any);

// Unique sender ID to filter out self-originated broadcast messages
const SENDER_ID = typeof window !== "undefined" ? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}` : "";

if (typeof window !== "undefined") {
  window.timerGlobalChannel = globalChannel;
}

// WebSocket handle + connection management
let ws: WebSocket | null = null;
let wsHeartbeatTimer: number | null = null;
let reconnectAttempts = 0;

function getCurrentHubId(): string | undefined {
  return typeof window !== "undefined" ? window.__currentHubId : undefined;
}

// Note: Timer state is persisted via individual endpoints (/api/timers/:id/start, /pause, /reset)

export const useTimerStore = create<TimerStoreState>((set, get) => {
  // ======== helpers ========
  const ensureHub = (): string | null => {
    const hubId = getCurrentHubId() || null;
    if (!hubId) console.warn("[TimerStore] No window.__currentHubId set — realtime will be disabled until you set it.");
    return hubId;
  };

  const formatAndReturn = (t: TimerState) => {
    return { ...t, formattedTime: formatTime(t.timeRemaining) };
  };

  // ======== Timer ticking helpers (defined early for use in mergeIncomingTimer) ========
  const startLocalCountdown = (id: TimerId, startedAtOverride?: number) => {
    if ((window as any).__timerIntervals === undefined) (window as any).__timerIntervals = {};
    const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals;

    // CRITICAL GUARD: Check if timer is paused BEFORE doing anything else
    const timerCheck = get().timers[id];
    if (timerCheck?.status === 'paused' || timerCheck?.isPaused === true) {
      console.log('[TimerStore] startLocalCountdown: BLOCKED - timer is paused:', id);
      // Clear any orphaned intervals for this paused timer
      if (globalIntervals[id]) {
        window.clearInterval(globalIntervals[id]);
        delete globalIntervals[id];
      }
      return;
    }

    // Clear any existing interval for this timer
    if (globalIntervals[id]) {
      window.clearInterval(globalIntervals[id]);
      delete globalIntervals[id];
    }

    // Get initial timer state
    const initialTimer = get().timers[id];
    if (!initialTimer) {
      console.warn('[TimerStore] startLocalCountdown: timer not found', id);
      return;
    }

    const hasFutureEnd = typeof initialTimer.endsAtMs === 'number' && initialTimer.endsAtMs > Date.now();
    if ((initialTimer.status === 'running' || initialTimer.isRunning) &&
        (initialTimer.timeRemaining <= 0 || !hasFutureEnd && typeof initialTimer.endsAtMs === 'number')) {
      console.log('[TimerStore] startLocalCountdown: refusing expired running timer', id);
      set((state) => ({
        timers: {
          ...state.timers,
          [id]: {
            ...state.timers[id],
            timeRemaining: 0,
            isRunning: false,
            isPaused: false,
            status: 'stopped' as const,
            startedAt: undefined,
            endsAtMs: undefined,
            formattedTime: formatTime(0),
          },
        },
      }));
      return;
    }

    // CRITICAL: Use timer.duration from persisted record - NO fallbacks for existing timers
    const duration = initialTimer.duration;
    if (!duration || duration <= 0) {
      console.error('[TimerStore] startLocalCountdown: ABORT - timer.duration is missing or invalid:', duration, 'timerId:', id);
      return;
    }
    
    console.log('[TimerStore] startLocalCountdown using timer.duration:', duration, 'image:', initialTimer.image);

    // Parse startedAt - ensure it's a valid millisecond timestamp
    let startedAt: number;
    if (startedAtOverride !== undefined) {
      // startedAtOverride is already in milliseconds
      startedAt = startedAtOverride;
    } else if (initialTimer.startedAt !== undefined) {
      // Parse existing startedAt (could be string ISO date or number)
      startedAt = typeof initialTimer.startedAt === 'string' 
        ? new Date(initialTimer.startedAt).getTime() 
        : initialTimer.startedAt;
    } else {
      // Calculate startedAt from current remaining time
      const timeRemaining = initialTimer.timeRemaining ?? duration;
      const elapsedSoFar = duration - timeRemaining;
      startedAt = Date.now() - (elapsedSoFar * 1000);
    }

    // Validate startedAt is a reasonable timestamp
    if (isNaN(startedAt) || startedAt <= 0) {
      console.warn('[TimerStore] startLocalCountdown: invalid startedAt, using now', startedAt);
      startedAt = Date.now();
    }

    // Update timer with valid startedAt and duration
    set((state) => ({
      timers: {
        ...state.timers,
        [id]: { ...state.timers[id], startedAt, duration }
      }
    }));

    console.log('[TimerStore] startLocalCountdown', id, 'startedAt:', new Date(startedAt).toISOString(), 'duration:', duration);

    globalIntervals[id] = window.setInterval(() => {
      const t = get().timers[id];
      const isTimerRunning = t?.status === 'running' || t?.isRunning === true;
      
      if (!t || !isTimerRunning) {
        if (globalIntervals[id]) {
          window.clearInterval(globalIntervals[id]);
          delete globalIntervals[id];
        }
        return;
      }

      // Calculate remaining time from startedAt for accuracy
      // Parse startedAt in case it changed
      const timerStartedAt = typeof t.startedAt === 'string' 
        ? new Date(t.startedAt).getTime() 
        : (t.startedAt ?? startedAt);
      // CRITICAL: Use timer's duration directly - NO fallbacks
      const timerDuration = t.duration;
      if (!timerDuration || timerDuration <= 0) {
        console.error('[TimerStore] interval tick: invalid duration, stopping', timerDuration);
        window.clearInterval(globalIntervals[id]);
        delete globalIntervals[id];
        return;
      }
      const elapsedSeconds = Math.floor((Date.now() - timerStartedAt) / 1000);
      const newTime = Math.max(0, timerDuration - elapsedSeconds);
      
      const updated: TimerState = {
        ...t,
        timeRemaining: newTime,
        lastUpdated: Date.now(),
        formattedTime: formatTime(newTime),
      };

      if (newTime <= 0) {
        const completionTimer = t;
        updated.isRunning = false;
        updated.isPaused = false;
        updated.status = 'stopped';
        updated.startedAt = undefined;
        if (globalIntervals[id]) {
          window.clearInterval(globalIntervals[id]);
          delete globalIntervals[id];
        }
        console.log('[TimerStore] Timer completed:', id);
        dispatchTimerCompleted(id, completionTimer, completionTimer.endsAtMs);
      }

      // Update Zustand state to trigger React re-render
      set((state) => ({ timers: { ...state.timers, [id]: updated } }));
    }, 1000) as unknown as number;
  };

  // Stop ticking for a timer (clear interval)
  const stopTick = (id: TimerId) => {
    if ((window as any).__timerIntervals === undefined) return;
    const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals;
    if (globalIntervals[id]) {
      console.log('[TimerStore] stopTick: clearing interval for', id);
      window.clearInterval(globalIntervals[id]);
      delete globalIntervals[id];
    }
  };

  // Hydrate a running timer from server endsAtMs - used for refresh scenarios and ALL timer types
  // CRITICAL: This is the SINGLE SOURCE OF TRUTH for timer countdown
  const hydrateRunningTimer = (id: TimerId, endsAtMs: number) => {
    // ===== GUARD 1: Validate id - be extra strict =====
    if (!id || id === 'undefined' || id === 'null' || (typeof id === 'string' && id.trim() === '')) {
      console.log('[TimerStore] hydrateRunningTimer: BLOCKED - invalid id:', id);
      return;
    }
    
    // ===== GUARD 2: Validate endsAtMs =====
    if (!endsAtMs || typeof endsAtMs !== 'number' || isNaN(endsAtMs)) {
      console.log('[TimerStore] hydrateRunningTimer: BLOCKED - invalid endsAtMs:', endsAtMs);
      return;
    }
    
    // ===== GUARD 3: Check if timer is paused in store =====
    const timer = get().timers[id];
    if (timer?.status === 'paused' || timer?.isPaused) {
      console.log('[TimerStore] hydrateRunningTimer: BLOCKED - timer is paused in store:', id);
      stopTick(id); // Ensure no orphaned intervals
      return;
    }
    
    
    if ((window as any).__timerIntervals === undefined) (window as any).__timerIntervals = {};
    const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals;
    
    // ===== GUARD 4: Don't re-hydrate if already ticking =====
    if (globalIntervals[id]) {
      console.log('[TimerStore] hydrateRunningTimer: BLOCKED - already ticking, skip', id);
      return;
    }
    
    // Calculate remaining from endsAtMs (server time) - NO duration fallback
    const remainingMs = Math.max(0, endsAtMs - Date.now());
    const remainingSecs = Math.floor(remainingMs / 1000);
    
    console.log('[TimerStore] hydrateRunningTimer START:', {
      id,
      endsAtMs,
      remainingMs,
      remainingSecs,
      existingIntervals: Object.keys(globalIntervals),
    });
    
    // ===== GUARD 5: Don't start if already expired =====
    if (remainingSecs <= 0) {
      const expiredTimer = get().timers[id];
      console.log('[TimerStore] hydrateRunningTimer: completing already expired timer', id);
      set((state) => ({
        timers: {
          ...state.timers,
          [id]: {
            ...state.timers[id],
            timeRemaining: 0,
            isRunning: false,
            isPaused: false,
            status: 'stopped' as const,
            startedAt: undefined,
            endsAtMs: undefined,
            formattedTime: formatTime(0),
          },
        },
      }));
      dispatchTimerCompleted(id, expiredTimer, endsAtMs);
      return;
    }
    
    // Update timer state - preserve existing duration/startedAt, only update running state
    set((state) => ({
      timers: {
        ...state.timers,
        [id]: {
          ...state.timers[id],
          isRunning: true,
          isPaused: false,
          status: 'running' as const,
          timeRemaining: remainingSecs,
          endsAtMs, // Store the authoritative end timestamp
          lastUpdated: Date.now(),
          formattedTime: formatTime(remainingSecs),
        }
      }
    }));
    
    // Start ticking interval
    globalIntervals[id] = window.setInterval(() => {
      const now = Date.now();
      const newRemainingMs = Math.max(0, endsAtMs - now);
      const newRemainingSecs = Math.floor(newRemainingMs / 1000);
      
      if (newRemainingSecs <= 0) {
        console.log('[TimerStore] hydrateRunningTimer: timer finished', id);
        const completionTimer = get().timers[id];
        window.clearInterval(globalIntervals[id]);
        delete globalIntervals[id];
        
        set((state) => ({
          timers: {
            ...state.timers,
            [id]: {
              ...state.timers[id],
              timeRemaining: 0,
              isRunning: false,
              isPaused: false,
              status: 'stopped' as const,
              endsAtMs: undefined,
              formattedTime: formatTime(0),
            }
          }
        }));
        dispatchTimerCompleted(id, completionTimer, endsAtMs);
        return;
      }
      
      console.log('[TimerStore] hydrateRunningTimer: tick update', id, 'remaining:', newRemainingSecs);
      set((state) => ({
        timers: {
          ...state.timers,
          [id]: {
            ...state.timers[id],
            timeRemaining: newRemainingSecs,
            formattedTime: formatTime(newRemainingSecs),
          }
        }
      }));
    }, 1000) as unknown as number;
    
    console.log('[TimerStore] hydrateRunningTimer: started ticking for', id);
  };

  // Merge incoming timer payload into store cleanly
  // CRITICAL: Preserve visual fields (image, title, etc) - only update session/status fields
  const mergeIncomingTimer = (incoming: Partial<TimerState> & { id: TimerId }) => {
    const existingTimer = get().timers[incoming.id];
    const wasRunning = existingTimer?.status === 'running' || existingTimer?.isRunning === true;
    const wasPaused = existingTimer?.status === 'paused' || existingTimer?.isPaused === true;
    const isNewTimer = !existingTimer;
    
    // ===== ORDERING CHECK: Protect running timers from stale pause messages =====
    // Rule: If store has RUNNING timer AND it's ACTIVELY TICKING, reject any pause
    // that doesn't have a STRICTLY NEWER lastUpdated timestamp.
    // This is a conservative approach that prioritizes keeping timers running.
    const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals ?? {};
    const isCurrentlyTicking = !!globalIntervals[incoming.id];
    const incomingStatus = incoming.status ?? (incoming.isRunning ? 'running' : incoming.isPaused ? 'paused' : 'idle');
    const incomingTimestamp = incoming.lastUpdated ?? 0;
    const existingTimestamp = existingTimer?.lastUpdated ?? 0;
    
    // GUARD: If currently RUNNING + TICKING and incoming is a PAUSE
    if (!isNewTimer && existingTimer?.status === 'running' && isCurrentlyTicking && incomingStatus === 'paused') {
      // Only accept this pause if it has a STRICTLY NEWER timestamp
      const hasNewerTimestamp = incomingTimestamp > 0 && incomingTimestamp > existingTimestamp;
      
      if (!hasNewerTimestamp) {
        console.log('[TimerStore] mergeIncomingTimer: REJECTING pause - running timer protected', {
          timerId: incoming.id,
          incomingTimestamp,
          existingTimestamp,
          hasNewerTimestamp,
          reason: 'Timer is running and ticking, pause must have strictly newer timestamp',
        });
        return; // Reject this pause - keep running
      }
      console.log('[TimerStore] mergeIncomingTimer: Accepting pause - has newer timestamp', {
        timerId: incoming.id,
        incomingTimestamp,
        existingTimestamp,
      });
    }
    
    // Generic stale check for all other cases (non-pause)
    if (!isNewTimer && incomingTimestamp > 0 && existingTimestamp > incomingTimestamp) {
      console.log('[TimerStore] mergeIncomingTimer: IGNORING STALE update', {
        timerId: incoming.id,
        incomingTimestamp,
        existingTimestamp,
        incomingStatus,
        existingStatus: existingTimer?.status,
      });
      return; // Stale message - ignore it
    }
    
    // Log image state BEFORE merge
    console.log('[TimerStore] mergeIncomingTimer BEFORE:', {
      timerId: incoming.id,
      existingImage: existingTimer?.image,
      incomingImage: incoming.image,
      existingDuration: existingTimer?.duration,
      incomingDuration: incoming.duration,
    });
    
    // Derive status from legacy flags if not provided
    // CRITICAL: Use nullish coalescing and check isPaused FIRST (paused takes precedence)
    const isPausedFromIncoming = incoming.isPaused ?? (incoming.status === 'paused');
    const isRunningFromIncoming = (incoming.isRunning ?? false) || incoming.status === 'running';
    let derivedStatus =
      incoming.status ??
      (isPausedFromIncoming ? 'paused' :
       isRunningFromIncoming ? 'running' :
       'idle');

    const incomingRemaining = incoming.timeRemaining ?? existingTimer?.timeRemaining ?? 0;
    const incomingHasFutureEnd = typeof incoming.endsAtMs === 'number' && incoming.endsAtMs > Date.now();
    const expiredRunningUpdate =
      derivedStatus === 'running' &&
      (incomingRemaining <= 0 || (typeof incoming.endsAtMs === 'number' && !incomingHasFutureEnd));
    if (expiredRunningUpdate) {
      derivedStatus = 'stopped';
    }
    
    // Build the base timer - use existing timer if available
    const baseTimer: TimerState = existingTimer ?? {
      id: incoming.id,
      duration: incoming.duration ?? incoming.timeRemaining ?? 0,
      timeRemaining: incoming.timeRemaining ?? incoming.duration ?? 0,
      isRunning: incoming.isRunning ?? false,
      isPaused: incoming.isPaused ?? false,
      status: derivedStatus,
      startedAt: incoming.startedAt,
      lastUpdated: incoming.lastUpdated ?? Date.now(),
      title: incoming.title,
      hubId: incoming.hubId ?? getCurrentHubId(),
    };

    // Calculate endsAtMs if timer is running
    let endsAtMs: number | undefined = incoming.endsAtMs;
    if (derivedStatus === 'running' && !endsAtMs) {
      // Calculate from timeRemaining if not provided
      const remaining = incoming.timeRemaining ?? baseTimer.timeRemaining ?? 0;
      endsAtMs = Date.now() + (remaining * 1000);
    }

    // CRITICAL: Only update session/status fields from incoming
    // Preserve existing visual fields (image, title, duration, etc.) unless explicitly provided
    const merged: TimerState = {
      ...baseTimer, // Start with existing timer data (preserves image, title, etc.)
      // CRITICAL: Explicitly set id to ensure it's never lost
      id: incoming.id,
      // Only update these specific fields from incoming:
      status: derivedStatus,
      isRunning: derivedStatus === 'running',
      isPaused: derivedStatus === 'paused',
      timeRemaining: incoming.timeRemaining ?? baseTimer.timeRemaining,
      startedAt: incoming.startedAt ?? (derivedStatus === 'running' ? (existingTimer?.startedAt ?? Date.now()) : undefined),
      // CRITICAL FIX: Clear endsAtMs for ALL non-running timers
      // Resume calculates fresh endsAtMs from timeRemaining - no need to preserve old one
      // Keeping endsAtMs on paused timers causes auto-start on page load
      endsAtMs: derivedStatus === 'running' ? endsAtMs : undefined,
      lastUpdated: incoming.lastUpdated ?? Date.now(),
      formattedTime: formatTime(incoming.timeRemaining ?? baseTimer.timeRemaining),
      // Preserve hubId from base, only override if explicitly provided
      hubId: incoming.hubId ?? baseTimer.hubId,
      // Only update title/image if explicitly provided in incoming (not undefined)
      ...(incoming.title !== undefined ? { title: incoming.title } : {}),
      ...(incoming.image !== undefined ? { image: incoming.image } : {}),
      ...(incoming.duration !== undefined ? { duration: incoming.duration } : {}),
      // Preserve type and segments for rectangular timers
      ...(incoming.type !== undefined ? { type: incoming.type } : {}),
      ...(incoming.segments !== undefined ? { segments: incoming.segments } : {}),
      ...(incoming.sessionId !== undefined ? { sessionId: incoming.sessionId } : {}),
      ...(incoming.completionKey !== undefined ? { completionKey: incoming.completionKey } : {}),
    };
    
    // Insert/update the timer in state
    set((state) => ({ timers: { ...state.timers, [merged.id]: merged } }));

    if (expiredRunningUpdate) {
      dispatchTimerCompleted(incoming.id, merged, incoming.endsAtMs);
    }
    
    // Log image state AFTER merge
    console.log('[TimerStore] mergeIncomingTimer AFTER:', {
      timerId: merged.id,
      mergedImage: merged.image,
      mergedDuration: merged.duration,
      mergedEndsAtMs: merged.endsAtMs,
      isNewTimer,
      wasRunning,
      derivedStatus,
    });
    
    // SIMPLIFIED: If timer should be running, ensure it's ticking
    // This handles ALL cases: new start, resume, refresh, cross-tab sync
    const isNowRunning = derivedStatus === 'running';
    
    // Compute endsAtMs from timeRemaining if not provided (fixes start broadcast issue)
    const timeRemaining = merged.timeRemaining ?? incoming.timeRemaining;
    const computedEndsAtMs = endsAtMs ?? (timeRemaining ? Date.now() + timeRemaining * 1000 : undefined);
    
    if (isNowRunning && computedEndsAtMs && computedEndsAtMs > Date.now()) {
      // Check if already ticking - don't double-start
      const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals ?? {};
      if (!globalIntervals[incoming.id]) {
        console.log('[TimerStore] Starting countdown for running timer', incoming.id, {
          endsAtMs,
          computedEndsAtMs,
          timeRemaining,
          wasRunning,
          wasPaused,
          isNewTimer,
        });
        // Also update the store with computed endsAtMs so it persists
        if (!endsAtMs && computedEndsAtMs) {
          set((state) => ({
            timers: {
              ...state.timers,
              [incoming.id]: {
                ...state.timers[incoming.id],
                endsAtMs: computedEndsAtMs,
              },
            },
          }));
        }
        hydrateRunningTimer(incoming.id, computedEndsAtMs);
      }
    }
    
    // Stop countdown if timer is no longer running - ALWAYS stop for paused/stopped/idle
    // (Stale messages are already filtered out by the ordering check above)
    const isNowStopped = derivedStatus === 'paused' || derivedStatus === 'stopped' || derivedStatus === 'idle';
    if (isNowStopped) {
      const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals ?? {};
      if (globalIntervals[incoming.id]) {
        console.log('[TimerStore] mergeIncomingTimer: Stopping countdown for', derivedStatus, 'timer', incoming.id);
        window.clearInterval(globalIntervals[incoming.id]);
        delete globalIntervals[incoming.id];
      }
    }
  };

  // Broadcast to BroadcastChannels (local + global)
  const broadcastLocal = (data: any) => {
    try {
      localChannel?.postMessage(data);
    } catch (e) {
      console.warn("[TimerStore] localChannel.postMessage failed:", e);
    }
    try {
      globalChannel?.postMessage(data);
    } catch (e) {
      console.warn("[TimerStore] globalChannel.postMessage failed:", e);
    }
  };

  // Send via WebSocket if connected
  const sendWs = (payload: any) => {
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(payload));
      } else {
        console.debug("[TimerStore] WebSocket not open; skipping send:", payload.type);
      }
    } catch (e) {
      console.warn("[TimerStore] sendWs error:", e);
    }
  };

  // ======== WebSocket initialization (hub-based) ========
  const initWebSocket = () => {
    const hubId = ensureHub();
    if (!hubId) return;

    // If ws already open/connecting for this hub, do nothing
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    // Use /ws path to avoid Vite HMR WebSocket conflict
    const wsUrl = `${protocol}//${location.host}/ws?hubId=${encodeURIComponent(hubId)}`;

    try {
      console.info("[TimerStore] Connecting WS →", wsUrl);
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.info("[TimerStore] WebSocket connected for hub:", hubId);
        console.log("[TimerStore] WebSocket readyState:", ws?.readyState, "URL:", wsUrl);
        reconnectAttempts = 0;

        // start heartbeat
        if (wsHeartbeatTimer) {
          window.clearInterval(wsHeartbeatTimer);
          wsHeartbeatTimer = null;
        }
        wsHeartbeatTimer = window.setInterval(() => {
          try {
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "ping", timestamp: Date.now() }));
            }
          } catch (e) {
            // ignore
          }
        }, PING_INTERVAL_MS) as unknown as number;
      };

      ws.onclose = (ev) => {
        console.warn("[TimerStore] WebSocket closed:", ev.code, ev.reason);
        if (wsHeartbeatTimer) {
          window.clearInterval(wsHeartbeatTimer);
          wsHeartbeatTimer = null;
        }
        // exponential backoff reconnect
        reconnectAttempts = Math.min(10, reconnectAttempts + 1);
        const backoff = RECONNECT_BASE_MS * Math.pow(2, reconnectAttempts - 1);
        setTimeout(() => initWebSocket(), backoff);
      };

      ws.onerror = (err) => {
        console.warn("[TimerStore] WebSocket error:", err);
      };

      ws.onmessage = (ev: MessageEvent) => {
        console.log('[TimerStore] WS raw message:', ev.data);
        try {
          // Safe JSON parse
          let msg: any;
          try {
            msg = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
          } catch (parseErr) {
            console.warn("[TimerStore] Failed to parse WS message:", parseErr);
            return;
          }
          
          console.log('[TimerStore] WS parsed message:', msg);
          
          // Handle pong/welcome messages if they have a type
          if (msg?.type === "pong" || msg?.type === "welcome" || msg?.type === "status") {
            console.debug("[TimerStore] WS meta message:", msg.type);
            return;
          }
          
          // Treat ALL other messages as timer updates
          // Backend sends: { timerId, hubId, status, session: { sessionId, title, status, duration, timeRemaining, startedAt } }
          const timerId = msg.timerId ?? msg.session?.timerId ?? msg.id;
          const session = msg.session || msg;
          
          if (!timerId) {
            console.warn("[TimerStore] WS message missing timerId:", msg);
            return;
          }
          
          // Normalize status from session
          const status = session.status || msg.status || 'idle';
          const isRunning = status === 'running';
          const isPaused = status === 'paused';
          
          // Use timerId directly as string or number (supports UUIDs)
          // Do NOT reject messages - allow hydration for new timers not yet in local state
          const normalizedId = timerId; // Keep as-is (string UUID or number)
          
          // Extract startedAt from session (server sends ISO string)
          const startedAtValue = session.startedAt 
            ? new Date(session.startedAt).getTime() 
            : (isRunning ? Date.now() : undefined);
          
          // Calculate endsAtMs - CRITICAL for running timer sync
          // Priority: msg.endsAtMs > session.endsAtMs > calculated from startedAt+duration
          let endsAtMs: number | undefined = msg.endsAtMs ?? session.endsAtMs;
          if (!endsAtMs && isRunning && startedAtValue) {
            const duration = session.duration ?? session.durationSeconds ?? session.totalDuration ?? 0;
            if (duration > 0) {
              endsAtMs = startedAtValue + (duration * 1000);
            }
          }
          // Fallback: calculate from timeRemaining if still no endsAtMs
          if (!endsAtMs && isRunning) {
            const remaining = session.timeRemaining ?? session.remainingDuration ?? session.duration;
            if (remaining && remaining > 0) {
              endsAtMs = Date.now() + (remaining * 1000);
            }
          }
          
          const payload = {
            id: normalizedId,
            duration: session.duration ?? session.durationSeconds ?? session.totalDuration,
            timeRemaining: session.timeRemaining ?? session.remainingDuration ?? session.duration,
            isRunning,
            isPaused,
            status,
             sessionId: session.sessionId ?? session.id ?? msg.sessionId,
             completionKey: (session.sessionId ?? session.id ?? msg.sessionId)
               ? `session:${session.sessionId ?? session.id ?? msg.sessionId}`
               : undefined,
            startedAt: startedAtValue,
            endsAtMs, // CRITICAL: Include for running timer sync
            lastUpdated: startedAtValue ?? Date.now(),
            title: session.title ?? msg.title,
            image: msg.image ?? session.image,
            hubId: msg.hubId ?? session.hubId ?? getCurrentHubId(),
          };
          
          console.log('[TimerStore] Normalized timer payload:', {
            ...payload,
            endsAtMs: payload.endsAtMs,
            hasValidEndsAtMs: !!payload.endsAtMs && payload.endsAtMs > Date.now(),
          });
          
          // Only apply if hub matches
          const currentHub = getCurrentHubId();
          if (payload.hubId && currentHub && payload.hubId !== currentHub) {
            console.debug("[TimerStore] Ignoring timer update for different hub:", payload.hubId, "current:", currentHub);
            return;
          }
          
          // Merge into store
          mergeIncomingTimer(payload as any);
          
          console.log('[TimerStore] Timer merged, status:', status);
        } catch (e) {
          console.error("[TimerStore] Error handling WS message:", e, ev);
        }
      };
    } catch (e) {
      console.error("[TimerStore] initWebSocket failed:", e);
    }
  };

  // ======== BroadcastChannel init ========
  const initBroadcast = () => {
    if (!localChannel) return;

    localChannel.onmessage = (ev: MessageEvent) => {
      try {
        const msg = ev.data;
        if (!msg || msg.type !== "timer-update") return;

        // CRITICAL: Ignore self-originated messages to prevent re-hydration loops
        if (msg.senderId === SENDER_ID) {
          return;
        }

        const hub = getCurrentHubId();
        if (msg.hubId && hub && msg.hubId !== hub) return;

        if (typeof msg.timerId !== "undefined") {
          console.log('[TimerStore] localChannel received (from other tab):', {
            timerId: msg.timerId,
            status: msg.status,
            isRunning: msg.isRunning,
            isPaused: msg.isPaused,
            endsAtMs: msg.endsAtMs,
          });
          
          mergeIncomingTimer({
            id: msg.timerId,
            timeRemaining: msg.timeRemaining,
            isRunning: msg.isRunning,
            isPaused: msg.isPaused,
            status: msg.status, // CRITICAL: Include status for pause/reset sync
             sessionId: msg.sessionId,
             completionKey: msg.completionKey,
            endsAtMs: msg.endsAtMs, // AUTHORITATIVE end timestamp
            lastUpdated: msg.lastUpdated ?? Date.now(),
            title: msg.title,
            image: msg.image,
            hubId: msg.hubId ?? hub,
          } as any);
        }
      } catch (e) {
        console.warn("[TimerStore] localChannel onmessage error:", e);
      }
    };

    // Global channel is optional but mirrors local traffic across different pages / contexts
    if (globalChannel) {
      globalChannel.onmessage = (ev: MessageEvent) => {
        try {
          const msg = ev.data;
          if (!msg || msg.type !== "timer-update") return;

          // CRITICAL: Ignore self-originated messages to prevent re-hydration loops
          if (msg.senderId === SENDER_ID) {
            return;
          }

          const hub = getCurrentHubId();
          if (msg.hubId && hub && msg.hubId !== hub) return;

          if (typeof msg.timerId !== "undefined") {
            console.log('[TimerStore] globalChannel received (from other tab):', {
              timerId: msg.timerId,
              status: msg.status,
              isRunning: msg.isRunning,
              isPaused: msg.isPaused,
              endsAtMs: msg.endsAtMs,
            });
            
            mergeIncomingTimer({
              id: msg.timerId,
              timeRemaining: msg.timeRemaining,
              isRunning: msg.isRunning,
              isPaused: msg.isPaused,
              status: msg.status, // CRITICAL: Include status for pause/reset sync
               sessionId: msg.sessionId,
               completionKey: msg.completionKey,
              endsAtMs: msg.endsAtMs, // AUTHORITATIVE end timestamp
              lastUpdated: msg.lastUpdated ?? Date.now(),
              title: msg.title,
              image: msg.image,
              hubId: msg.hubId ?? hub,
            } as any);
          }
        } catch (e) {
          console.warn("[TimerStore] globalChannel onmessage error:", e);
        }
      };
    }
  };

  // ======== Broadcast & send helper (hub-aware) ========
  const broadcastTimerUpdate = (timerId: TimerId, timerData?: Partial<TimerState>) => {
    const hubId = getCurrentHubId();
    const timer = get().timers[timerId];
    if (!timer && !timerData) {
      return;
    }

    const payload = {
      type: "timer-update",
      senderId: SENDER_ID, // For filtering self-originated broadcasts
      timerId,
      timeRemaining: timerData?.timeRemaining ?? timer?.timeRemaining,
      remainingSeconds: timerData?.timeRemaining ?? timer?.timeRemaining,
      isRunning: timerData?.isRunning ?? timer?.isRunning,
      isPaused: timerData?.isPaused ?? timer?.isPaused,
      status: timerData?.status ?? timer?.status,
      sessionId: timerData?.sessionId ?? timer?.sessionId,
      completionKey: timerData?.completionKey ?? timer?.completionKey,
      endsAtMs: timerData?.endsAtMs ?? timer?.endsAtMs, // AUTHORITATIVE end timestamp
      lastUpdated: timerData?.lastUpdated ?? Date.now(),
      title: timerData?.title ?? timer?.title,
      duration: timerData?.duration ?? timer?.duration,
      hubId: hubId,
      uniqueTimerId: `timer-${timerId}`,
    };

    console.log('[TimerStore] broadcastTimerUpdate SENDING:', {
      timerId,
      status: payload.status,
      isRunning: payload.isRunning,
      isPaused: payload.isPaused,
      endsAtMs: payload.endsAtMs,
      timeRemaining: payload.timeRemaining,
    });

    // Broadcast locally (other tabs)
    broadcastLocal(payload);

    // Send to server via WebSocket (server will redistribute to hub subscribers)
    sendWs(payload);
  };

  // ======== Public methods ========
  const createTimer = async (timerData: Partial<TimerState> & { id: TimerId; duration: number; title?: string; }) => {
    const hubId = ensureHub();
    const t: TimerState = {
      id: timerData.id,
      duration: timerData.duration,
      timeRemaining: timerData.duration,
      isRunning: false,
      isPaused: false,
      status: 'idle',
      lastUpdated: Date.now(),
      title: timerData.title,
      hubId: hubId ?? undefined,
      formattedTime: formatTime(timerData.duration),
    };

    set((state) => ({ timers: { ...state.timers, [t.id]: t } }));


    // Broadcast locally and to server
    broadcastTimerUpdate(t.id);
  };

  const startTimer = async (id: TimerId) => {
    console.log('[TimerStore] startTimer called:', id);
    
    const hubId = ensureHub();
    if (!hubId) {
      console.warn('[TimerStore] startTimer: no hubId, aborting');
      return;
    }

    const t = get().timers[id];
    if (!t) {
      console.warn("[TimerStore] startTimer: timer not found", id);
      return;
    }
    
    // CRITICAL FIX: DO NOT skip if already running - resume must ALWAYS broadcast
    // The old check `if (t.isRunning) return` broke pause→resume sync
    console.log('[TimerStore] startTimer: proceeding with start/resume, timer state:', {
      id,
      isRunning: t.isRunning,
      isPaused: t.isPaused,
      status: t.status,
      timeRemaining: t.timeRemaining,
    });

    // Calculate endsAtMs from remaining time (handles both fresh start and resume)
    const now = Date.now();
    const remainingMs = (t.timeRemaining > 0 ? t.timeRemaining : t.duration) * 1000;
    const endsAtMs = now + remainingMs;
    const startedAt = now - ((t.duration * 1000) - remainingMs);

    // Set running locally with startedAt and endsAtMs
    const running: TimerState = {
      ...t,
      isRunning: true,
      isPaused: false,
      status: 'running',
      startedAt,
      endsAtMs,
      completionKey: t.sessionId ? `session:${t.sessionId}` : `ends:${endsAtMs}`,
      lastUpdated: now,
    };
    set((state) => ({ timers: { ...state.timers, [id]: running } }));

    // ALWAYS broadcast - resume is a start, period
    console.log('[TimerStore] startTimer: broadcasting running state with endsAtMs:', endsAtMs);
    broadcastTimerUpdate(id, running);

    // Start local countdown for ALL timer types using endsAtMs
    hydrateRunningTimer(id, endsAtMs);
  };

  const pauseTimer = async (id: TimerId) => {
    const hubId = ensureHub();
    if (!hubId) return;

    const t = get().timers[id];
    if (!t) {
      console.warn("[TimerStore] pauseTimer: timer not found", id);
      return;
    }

    // stop local interval
    if ((window as any).__timerIntervals?.[id]) {
      window.clearInterval((window as any).__timerIntervals[id]);
      delete (window as any).__timerIntervals[id];
    }

    // When pausing, capture current timeRemaining accurately from startedAt
    const now = Date.now();
    let finalTimeRemaining = t.timeRemaining;
    
    // If timer was running with startedAt, calculate accurate remaining time
    if (t.isRunning && t.startedAt) {
      const elapsedSeconds = Math.floor((now - t.startedAt) / 1000);
      finalTimeRemaining = Math.max(0, t.duration - elapsedSeconds);
    }
    
    const paused: TimerState = {
      ...t,
      isRunning: false,
      isPaused: true,
      status: 'paused',
      timeRemaining: finalTimeRemaining,
      startedAt: undefined, // Clear startedAt when paused
      endsAtMs: undefined,  // CRITICAL: Clear endsAtMs to prevent auto-resume on page load
      lastUpdated: now,
    };

    set((state) => ({ timers: { ...state.timers, [id]: paused } }));
    broadcastTimerUpdate(id, paused);

    // Note: Server API call is handled by the component with auth headers
  };

  const resetTimer = async (id: TimerId) => {
    const hubId = ensureHub();
    if (!hubId) return;

    const t = get().timers[id];
    if (!t) return;

    // clear intervals
    if ((window as any).__timerIntervals?.[id]) {
      window.clearInterval((window as any).__timerIntervals[id]);
      delete (window as any).__timerIntervals[id];
    }

    const reset: TimerState = {
      ...t,
      timeRemaining: t.duration,
      isRunning: false,
      isPaused: false,
      status: 'idle',
      startedAt: undefined, // Clear startedAt on reset
      endsAtMs: undefined,  // Clear endsAtMs to prevent auto-resume on page load
      lastUpdated: Date.now(),
      formattedTime: formatTime(t.duration),
    };

    set((state) => ({ timers: { ...state.timers, [id]: reset } }));
    broadcastTimerUpdate(id, reset);

    // Note: Server API call is handled by the component with auth headers
  };

  const restartTimer = async (id: TimerId) => {
    const hubId = ensureHub();
    if (!hubId) return;

    // restart semantics: set to duration & start immediately
    const t = get().timers[id];
    if (!t) return;

    const now = Date.now();
    const endsAtMs = now + (t.duration * 1000); // AUTHORITATIVE end timestamp
    
    const restarted: TimerState = {
      ...t,
      timeRemaining: t.duration,
      isRunning: true,
      isPaused: false,
      status: 'running',
      startedAt: now, // Fresh start
      endsAtMs,
      // A restart creates a new server session. Keep timestamp identity locally
      // until the response-backed session ID is merged by the page.
      sessionId: undefined,
      completionKey: `ends:${endsAtMs}`,
      lastUpdated: now,
      formattedTime: formatTime(t.duration),
    };

    set((state) => ({ timers: { ...state.timers, [id]: restarted } }));
    broadcastTimerUpdate(id, restarted);

    // Start local countdown for ALL timer types using endsAtMs
    hydrateRunningTimer(id, endsAtMs);

    // Note: Server API call is handled by the component with auth headers
  };

  const updateTimer = async (id: TimerId, updates: Partial<TimerState>) => {
    const hubId = ensureHub();
    if (!hubId) return;

    set((state) => {
      const t = state.timers[id];
      if (!t) {
        console.warn("[TimerStore] updateTimer: timer not found", id);
        return state;
      }
      const merged: TimerState = formatAndReturn({
        ...t,
        ...updates,
        lastUpdated: Date.now(),
      } as TimerState);

      return { timers: { ...state.timers, [id]: merged } };
    });

    const updated = get().timers[id];
    if (updated) {
      broadcastTimerUpdate(id, updated);
    }
  };

  const updateTimerState = (id: TimerId, updates: Partial<TimerState>) => {
    // Lightweight local update (used by WS & BroadcastChannel)
    const now = Date.now();
    let expiredCompletionTimer: TimerState | null = null;
    
    set((state) => {
      const t = state.timers[id];
      if (!t) {
        // create a minimal timer if we don't have it yet
        // Derive status from isRunning/isPaused if not provided
        let derivedStatus = updates.status ??
          (updates.isRunning ? 'running' : 
           updates.isPaused ? 'paused' : 
           'idle');
        const requestedRemaining = updates.timeRemaining ?? updates.duration ?? 0;
        const hasFutureEnd = typeof updates.endsAtMs === 'number' && updates.endsAtMs > now;
        const expiredRunningUpdate =
          derivedStatus === 'running' && requestedRemaining <= 0 && !hasFutureEnd;
        if (expiredRunningUpdate) derivedStatus = 'stopped';
        
        console.log('[TimerStore] updateTimerState CREATE:', {
          id,
          derivedStatus,
          'updates.status': updates.status,
          'updates.isRunning': updates.isRunning,
          'updates.isPaused': updates.isPaused,
        });
        
        // Calculate startedAt for running timer
        const startedAt = derivedStatus === 'running' 
          ? (updates.startedAt ?? now) 
          : undefined;
        
        const created: TimerState = formatAndReturn({
          id,
          duration: updates.duration ?? updates.timeRemaining ?? 0,
          timeRemaining: updates.timeRemaining ?? updates.duration ?? 0,
           isRunning: derivedStatus === 'running',
           isPaused: derivedStatus === 'paused',
          status: derivedStatus,
          startedAt,
          lastUpdated: updates.lastUpdated ?? now,
          title: updates.title,
          hubId: updates.hubId ?? getCurrentHubId(),
          sessionId: updates.sessionId,
          completionKey: updates.completionKey
            ?? (updates.sessionId ? `session:${updates.sessionId}` : undefined)
            ?? (typeof updates.endsAtMs === 'number' ? `ends:${updates.endsAtMs}` : undefined)
            ?? (updates.startedAt !== undefined ? `started:${String(updates.startedAt)}` : undefined),
        } as TimerState);

        if (expiredRunningUpdate) expiredCompletionTimer = created;
        
        // If timer is running, start local countdown
        if (derivedStatus === 'running') {
          console.log('[TimerStore] updateTimerState: STARTING COUNTDOWN for running timer:', id);
          // Schedule countdown start after state update
          setTimeout(() => startLocalCountdown(id, startedAt), 0);
        } else {
          console.log('[TimerStore] updateTimerState: NOT starting countdown, status:', derivedStatus);
        }
        
        return { timers: { ...state.timers, [id]: created } };
      }
      
      // Merge with existing timer
      const wasRunning = t.status === 'running' || t.isRunning === true;
      const requestedStatus = updates.status ?? (updates.isRunning ? 'running' : updates.isPaused ? 'paused' : undefined);
      const requestedRemaining = updates.timeRemaining ?? t.timeRemaining;
      const hasFutureEnd = typeof updates.endsAtMs === 'number' && updates.endsAtMs > now;
      const expiredRunningUpdate =
        requestedStatus === 'running' &&
        (requestedRemaining <= 0 || (typeof updates.endsAtMs === 'number' && !hasFutureEnd));
      const effectiveUpdates = expiredRunningUpdate
        ? {
            ...updates,
            status: 'stopped' as const,
            isRunning: false,
            isPaused: false,
            timeRemaining: 0,
            startedAt: undefined,
            endsAtMs: undefined,
            completionKey: updates.completionKey
              ?? (updates.sessionId ? `session:${updates.sessionId}` : undefined)
              ?? (typeof updates.endsAtMs === 'number' ? `ends:${updates.endsAtMs}` : undefined)
              ?? (updates.startedAt !== undefined ? `started:${String(updates.startedAt)}` : undefined)
              ?? t.completionKey,
          }
        : {
            ...updates,
            ...(updates.completionKey || updates.sessionId || updates.endsAtMs !== undefined || updates.startedAt !== undefined
              ? {
                  completionKey: updates.completionKey
                    ?? (updates.sessionId ? `session:${updates.sessionId}` : undefined)
                    ?? (typeof updates.endsAtMs === 'number' ? `ends:${updates.endsAtMs}` : undefined)
                    ?? (updates.startedAt !== undefined ? `started:${String(updates.startedAt)}` : undefined),
                }
              : {}),
          };
      const isNowRunning = (effectiveUpdates.status === 'running') || (effectiveUpdates.isRunning === true) ||
        (effectiveUpdates.status === undefined && effectiveUpdates.isRunning === undefined && wasRunning);
      
      // Calculate startedAt for merge
      const mergedStartedAt = isNowRunning 
        ? (effectiveUpdates.startedAt ?? t.startedAt ?? now)
        : undefined;
      
      const merged: TimerState = formatAndReturn({ 
        ...t, 
        ...effectiveUpdates,
        startedAt: mergedStartedAt,
        lastUpdated: now 
      } as TimerState);

      if (expiredRunningUpdate) expiredCompletionTimer = merged;
      
      // Start countdown if transitioning to running
      if (isNowRunning && !wasRunning) {
        setTimeout(() => startLocalCountdown(id, mergedStartedAt), 0);
      }
      
      // Stop countdown if transitioning away from running
      if (!isNowRunning && wasRunning) {
        const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals ?? {};
        if (globalIntervals[id]) {
          window.clearInterval(globalIntervals[id]);
          delete globalIntervals[id];
        }
      }
      
      return { timers: { ...state.timers, [id]: merged } };
    });

    if (expiredCompletionTimer) {
      dispatchTimerCompleted(id, expiredCompletionTimer, expiredCompletionTimer.endsAtMs);
    }
  };

  const getTimer = (id: TimerId) => {
    return get().timers[id];
  };

  const clearTimer = (id: TimerId) => {
    // clear interval if any
    if ((window as any).__timerIntervals?.[id]) {
      window.clearInterval((window as any).__timerIntervals[id]);
      delete (window as any).__timerIntervals[id];
    }
    set((state) => {
      const { [id]: removed, ...rest } = state.timers;
      return { timers: rest };
    });
  };

  const deleteTimer = async (id: TimerId) => {
    // Note: Server delete API call is handled by the component with auth headers
    // Just clear local state
    clearTimer(id);
  };

  // ======== Initialize WS & Broadcast on store creation ========
  if (typeof window !== "undefined") {
    // Defer initialization slightly to allow window.__currentHubId to be set by hub loader
    setTimeout(() => {
      initBroadcast();
      // only initialize WS if hubId exists
      if (getCurrentHubId()) initWebSocket();
    }, 50);
    
    // ======== Page visibility listener for re-sync on tab return ========
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        console.log('[TimerStore] Tab became visible - re-syncing running timers');
        const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals ?? {};
        const timers = get().timers;
        
        // Check each timer with an endsAtMs but no active interval
        for (const [id, timer] of Object.entries(timers)) {
          // GUARD: Skip invalid entries - be extra strict about ID
          if (!id || id === 'undefined' || id === 'null' || !timer) {
            console.log('[TimerStore] Visibility re-sync: skipping invalid entry, id:', id);
            continue;
          }
          
          // GUARD: Skip paused timers - NEVER restart paused timers
          if (timer.status === 'paused' || timer.isPaused) {
            console.log('[TimerStore] Visibility re-sync: skipping PAUSED timer:', id);
            // Ensure no orphaned interval
            if (globalIntervals[id]) {
              window.clearInterval(globalIntervals[id]);
              delete globalIntervals[id];
            }
            continue;
          }
          
          // GUARD: Skip timers without valid endsAtMs
          if (!timer.endsAtMs || typeof timer.endsAtMs !== 'number') {
            console.log('[TimerStore] Visibility re-sync: skipping timer with invalid endsAtMs:', id, timer.endsAtMs);
            continue;
          }
          
          // Only re-hydrate running timers with valid endsAtMs and no active interval
          if (timer.status === 'running' && !globalIntervals[id]) {
            const remainingMs = timer.endsAtMs - Date.now();
            if (remainingMs > 0) {
              console.log('[TimerStore] Visibility re-sync: re-hydrating running timer:', id, 'endsAtMs:', timer.endsAtMs);
              hydrateRunningTimer(id as any, timer.endsAtMs);
            } else {
              console.log('[TimerStore] Visibility re-sync: completing timer expired while tab was hidden:', id);
              set((state) => ({
                timers: {
                  ...state.timers,
                  [id]: {
                    ...state.timers[id],
                    timeRemaining: 0,
                    isRunning: false,
                    isPaused: false,
                    status: 'stopped' as const,
                    startedAt: undefined,
                    endsAtMs: undefined,
                    formattedTime: formatTime(0),
                  },
                },
              }));
              dispatchTimerCompleted(id, timer, timer.endsAtMs);
            }
          }
        }
      }
    });
  }

  // ======== Return public API ========
  return {
    timers: {},

    initWebSocket,
    initBroadcast,

    createTimer,
    startTimer,
    pauseTimer,
    resetTimer,
    restartTimer,
    updateTimer,
    updateTimerState,
    getTimer,
    clearTimer,
    deleteTimer,

    broadcastTimerUpdate: (timerId: TimerId, timerData?: Partial<TimerState>) => {
      broadcastTimerUpdate(timerId, timerData);
    },
    
    startLocalCountdown,
    
    // Check if a timer has an active ticking interval
    isTicking: (id: TimerId): boolean => {
      const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals || {};
      return !!globalIntervals[id];
    },
    
    
    // Expose the single authoritative hydration/completion implementation.
    hydrateRunningTimer,
    
    // Seed timer state from hydration endpoint session - using endsAtMs as authoritative
    seedFromSession: (session) => {
      const timerId = session.timerId;
      const hubId = getCurrentHubId();
      const globalIntervals: Record<string | number, number> = (window as any).__timerIntervals ?? {};
      
      // Calculate duration in seconds
      const durationSecs = session.durationSeconds || 
        (session.totalDuration ? Math.floor(session.totalDuration / 1000) : 0);
      
      // Calculate endsAtMs (authoritative) and remaining time
      let endsAtMs: number | undefined;
      let timeRemaining = session.timeRemaining ?? durationSecs;
      
      if (session.status === 'running') {
        // Prefer server-provided endsAtMs if available
        if (session.endsAtMs) {
          endsAtMs = session.endsAtMs;
          timeRemaining = Math.max(0, Math.floor((endsAtMs - Date.now()) / 1000));
        } else if (session.startedAt) {
          // Fallback: calculate from startedAt + duration
          const startedAtMs = new Date(session.startedAt).getTime();
          endsAtMs = startedAtMs + (durationSecs * 1000);
          timeRemaining = Math.max(0, Math.floor((endsAtMs - Date.now()) / 1000));
        }
      }
      
      // CRITICAL: Get existing timer to preserve metadata like image
      const existingTimer = get().timers[timerId];
      
      console.log('[TimerStore] seedFromSession:', {
        timerId,
        durationSecs,
        timeRemaining,
        endsAtMs,
        status: session.status,
        sessionImage: session.image,
        existingImage: existingTimer?.image,
      });
      
      // CRITICAL: Clear endsAtMs for non-running timers to prevent auto-start
      const safeEndsAtMs = session.status === 'running' ? endsAtMs : undefined;
      const safeStartedAt = session.status === 'running' && session.startedAt 
        ? new Date(session.startedAt).getTime() 
        : undefined;
      const sessionCompletionKey = session.sessionId
        ? `session:${session.sessionId}`
        : endsAtMs !== undefined
          ? `ends:${endsAtMs}`
          : undefined;

      if (session.status === 'running' && (timeRemaining <= 0 || (endsAtMs !== undefined && endsAtMs <= Date.now()))) {
        const expiredTimer: TimerState = {
          ...existingTimer,
          id: timerId,
          duration: durationSecs,
          timeRemaining: 0,
          endsAtMs: undefined,
          isRunning: false,
          isPaused: false,
          status: 'stopped',
          title: session.title || existingTimer?.title,
          image: session.image || existingTimer?.image,
          hubId,
          sessionId: session.sessionId || existingTimer?.sessionId,
          completionKey: sessionCompletionKey || existingTimer?.completionKey,
          startedAt: undefined,
          lastUpdated: Date.now(),
          formattedTime: formatTime(0),
        };

        set((state) => ({
          timers: {
            ...state.timers,
            [timerId]: expiredTimer,
          },
        }));
        dispatchTimerCompleted(timerId, expiredTimer, endsAtMs);
        return;
      }
      
      // Update timer state - MERGE with existing timer to preserve image
      set((state) => ({
        timers: {
          ...state.timers,
          [timerId]: {
            ...state.timers[timerId], // Preserve existing metadata
            id: timerId,
            duration: durationSecs,
            timeRemaining,
            endsAtMs: safeEndsAtMs, // Only set for running timers
            isRunning: session.status === 'running',
            isPaused: session.status === 'paused',
            status: session.status as 'running' | 'paused' | 'stopped' | 'idle',
            title: session.title || state.timers[timerId]?.title,
            image: session.image || state.timers[timerId]?.image, // Prefer session, fallback to existing
            hubId: hubId,
            sessionId: session.sessionId || state.timers[timerId]?.sessionId,
            completionKey: sessionCompletionKey || state.timers[timerId]?.completionKey,
            startedAt: safeStartedAt, // Only set for running timers
            lastUpdated: Date.now(),
            formattedTime: formatTime(timeRemaining),
          }
        }
      }));
      
      // CRITICAL: Handle paused timers - NEVER hydrate, stop any orphaned intervals
      if (session.status === 'paused') {
        console.log('[TimerStore] seedFromSession: timer is PAUSED, ensuring no ticking:', timerId);
        if (globalIntervals[timerId]) {
          window.clearInterval(globalIntervals[timerId]);
          delete globalIntervals[timerId];
        }
        return; // NEVER call hydrateRunningTimer for paused timers
      }
      
      // CRITICAL: Start countdown using hydrateRunningTimer if timer is running
      // Don't re-hydrate if already ticking
      if (session.status === 'running' && endsAtMs && !globalIntervals[timerId]) {
        console.log('[TimerStore] seedFromSession: hydrating running timer', timerId, 'endsAtMs:', endsAtMs);
        // Use setTimeout to ensure state is committed before hydrating
        // Call the local hydrateRunningTimer function directly (defined earlier in this scope)
        const finalEndsAtMs = endsAtMs; // Capture for closure
        setTimeout(() => {
          hydrateRunningTimer(timerId, finalEndsAtMs);
        }, 0);
      }
    },
  };
});

// Export singleton utility for external scripts
export const timerStoreApi = {
  updateTimer: (id: number, updates: Partial<TimerState>) => useTimerStore.getState().updateTimerState(id, updates),
  getTimer: (id: number) => useTimerStore.getState().getTimer(id),
  getState: () => useTimerStore.getState(),
};
